import { useState } from 'react';
import { Button, OverlayTrigger, Popover, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import type { ProjectJob, ProjectResult } from '../api/types';
import { useLazyGetProjectJobQuery } from '../api/indexerApi';
import { INSPECT_MAX_URLS_PER_JOB } from '../lib/inspect-limits';
import { navigateToInspectWithPrefill } from '../lib/url-list-prefill';
import { copyText, decodeUrlForDisplay, formatFaNumber } from '../lib/utils';
import { guideTooltipHtml } from './CoverageHelpTip';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import type { BulkNotify } from './InspectBulkMenu';
import { IndexerIcon } from './IndexerIcon';

export type { BulkNotify };

type IndexBulkAction = 'copySuccess' | 'copyFailed' | 'copyAll' | 'inspectStatus';

type BulkActionGuide = {
  title: string;
  paragraphs: string[];
};

const INDEX_BULK_ACTION_GUIDES = {
  copySuccess: {
    title: 'کپی موفق‌ها',
    paragraphs: [
      'تمام آدرس‌هایی که درخواست ایندکس برایشان با موفقیت به گوگل ارسال شده را در کلیپ‌بورد کپی می‌کند.',
      'هر URL در یک خط قرار می‌گیرد.',
      'با عدد «موفق» در ستون نتیجه هم‌خوان است.',
    ],
  },
  copyFailed: {
    title: 'کپی ناموفق‌ها',
    paragraphs: [
      'تمام آدرسی که درخواست ایندکس برایش ناموفق بوده را کپی می‌کند.',
      'مثلاً به‌خاطر سهمیه، کلید، دسترسی یا خطای شبکه.',
      'با عدد «ناموفق» در ستون نتیجه هم‌خوان است.',
    ],
  },
  copyAll: {
    title: 'کپی همه آدرس‌ها',
    paragraphs: [
      'تمام آدرس‌های همین درخواست را بدون توجه به موفق یا ناموفق بودن کپی می‌کند.',
      'هر URL در یک خط قرار می‌گیرد.',
    ],
  },
  inspectStatus: {
    title: 'بررسی وضعیت ایندکس',
    paragraphs: [
      'تمام آدرس‌های همین درخواست را به فرم «وضعیت ایندکس» منتقل می‌کند.',
      'بعد از انتقال می‌توانید وضعیت ایندکس هر آدرس را در Search Console بررسی کنید.',
      `حداکثر ${formatFaNumber(INSPECT_MAX_URLS_PER_JOB)} آدرس در فرم قرار می‌گیرد.`,
    ],
  },
} as const satisfies Record<IndexBulkAction, BulkActionGuide>;

function overlayContainer() {
  return document.body;
}

function BulkActionHelp({ action }: { action: IndexBulkAction }) {
  const guide = INDEX_BULK_ACTION_GUIDES[action];
  return (
    <span
      className="history-bulk-help-btn"
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <HelpTooltip
        html={guideTooltipHtml(guide)}
        placement="left"
        className="ms-1"
      />
    </span>
  );
}

function urlsFromIndexResults(
  results: ProjectResult[],
  status: 'success' | 'failed' | 'all',
): string[] {
  const filtered = results.filter((item) => {
    if (!item.url) return false;
    if (status === 'all') return true;
    if (status === 'success') return item.success === true;
    return item.success === false;
  });
  return [...new Set(filtered.map((item) => item.url))];
}

export function IndexBulkMenu({
  menuId,
  successCount,
  failedCount,
  totalCount,
  resolveUrls,
  onNotify,
}: {
  menuId: string;
  successCount: number;
  failedCount: number;
  totalCount: number;
  resolveUrls: (
    status: 'success' | 'failed' | 'all',
  ) => Promise<string[]> | string[];
  onNotify?: BulkNotify;
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  async function runAction(action: IndexBulkAction) {
    if (busy) return;
    setBusy(true);
    try {
      const status =
        action === 'copySuccess'
          ? 'success'
          : action === 'copyFailed'
            ? 'failed'
            : 'all';
      const urls = await resolveUrls(status);

      if (urls.length === 0) {
        onNotify?.({
          variant: 'warning',
          text:
            action === 'copySuccess'
              ? 'در این درخواست آدرس موفقی نیست.'
              : action === 'copyFailed'
                ? 'در این درخواست آدرس ناموفقی نیست.'
                : 'در این درخواست آدرسی نیست.',
        });
        return;
      }

      if (action === 'inspectStatus') {
        const limited = urls.slice(0, INSPECT_MAX_URLS_PER_JOB);
        navigateToInspectWithPrefill(navigate, limited, 'index-history');
        onNotify?.({
          variant: 'info',
          text:
            limited.length < urls.length
              ? `${formatFaNumber(limited.length)} آدرس به فرم وضعیت ایندکس منتقل شد (حداکثر ${formatFaNumber(INSPECT_MAX_URLS_PER_JOB)}).`
              : `${formatFaNumber(limited.length)} آدرس به فرم وضعیت ایندکس منتقل شد.`,
        });
        return;
      }

      const text = urls.map(decodeUrlForDisplay).join('\n');
      const ok = await copyText(text);
      onNotify?.({
        variant: ok ? 'success' : 'danger',
        text: ok
          ? action === 'copySuccess'
            ? `${formatFaNumber(urls.length)} آدرس موفق کپی شد.`
            : action === 'copyFailed'
              ? `${formatFaNumber(urls.length)} آدرس ناموفق کپی شد.`
              : `${formatFaNumber(urls.length)} آدرس کپی شد.`
          : 'کپی در کلیپ‌بورد ناموفق بود.',
      });
    } catch (err) {
      onNotify?.({
        variant: 'danger',
        text:
          err instanceof Error
            ? err.message
            : 'خواندن نتایج درخواست ناموفق بود.',
      });
    } finally {
      setBusy(false);
    }
  }

  const menu = (
    <Popover
      id={`index-bulk-menu-${menuId}`}
      className="history-bulk-popover"
      onClick={(event) => event.stopPropagation()}
    >
      <Popover.Body className="history-bulk-popover-body">
        <div className="history-bulk-menu-item">
          <button
            type="button"
            className="history-bulk-action"
            disabled={successCount === 0 || busy}
            onClick={(event) => {
              event.stopPropagation();
              void runAction('copySuccess');
            }}
          >
            کپی موفق‌ها
          </button>
          <BulkActionHelp action="copySuccess" />
        </div>
        <div className="history-bulk-menu-item">
          <button
            type="button"
            className="history-bulk-action"
            disabled={failedCount === 0 || busy}
            onClick={(event) => {
              event.stopPropagation();
              void runAction('copyFailed');
            }}
          >
            کپی ناموفق‌ها
          </button>
          <BulkActionHelp action="copyFailed" />
        </div>
        <div className="history-bulk-menu-item">
          <button
            type="button"
            className="history-bulk-action"
            disabled={totalCount === 0 || busy}
            onClick={(event) => {
              event.stopPropagation();
              void runAction('inspectStatus');
            }}
          >
            بررسی وضعیت ایندکس
          </button>
          <BulkActionHelp action="inspectStatus" />
        </div>
        <div className="history-bulk-menu-item">
          <button
            type="button"
            className="history-bulk-action"
            disabled={totalCount === 0 || busy}
            onClick={(event) => {
              event.stopPropagation();
              void runAction('copyAll');
            }}
          >
            کپی همه آدرس‌ها
          </button>
          <BulkActionHelp action="copyAll" />
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      rootClose
      placement="bottom-start"
      container={overlayContainer}
      overlay={menu}
    >
      <Button
        variant="outline-secondary"
        size="sm"
        className="history-bulk-menu-toggle d-inline-flex align-items-center me-2 fs--2"
        aria-label="عملیات"
        disabled={busy}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {busy ? (
          <Spinner
            size="sm"
            animation="border"
            aria-hidden
            style={{ width: 12, height: 12 }}
          />
        ) : (
          <>
            <IndexerIcon name="collection" className="me-1" />
            <span>عملیات</span>
            <IndexerIcon name="chevron-down" className="ms-1" />
          </>
        )}
      </Button>
    </OverlayTrigger>
  );
}

export function isIndexJobKind(kind: ProjectJob['kind']) {
  return kind === 'google_index' || kind === 'indexnow';
}

export function IndexJobBulkMenu({
  job,
  projectId,
  onNotify,
}: {
  job: ProjectJob;
  projectId: string;
  onNotify?: BulkNotify;
}) {
  const [getProjectJob] = useLazyGetProjectJobQuery();
  return (
    <IndexBulkMenu
      menuId={job.id}
      successCount={job.successCount}
      failedCount={job.failedCount}
      totalCount={job.total ?? 0}
      resolveUrls={async (status) => {
        const full = await getProjectJob(
          { projectId, jobId: job.id },
          true,
        ).unwrap();
        return urlsFromIndexResults(full.results ?? [], status);
      }}
      onNotify={onNotify}
    />
  );
}

export function IndexResultsBulkMenu({
  results,
  successCount,
  failedCount,
  onNotify,
}: {
  results: ProjectResult[];
  successCount: number;
  failedCount: number;
  onNotify?: BulkNotify;
}) {
  return (
    <IndexBulkMenu
      menuId="job-results"
      successCount={successCount}
      failedCount={failedCount}
      totalCount={results.length}
      resolveUrls={(status) => urlsFromIndexResults(results, status)}
      onNotify={onNotify}
    />
  );
}
